import PropTypes from "prop-types";
import React, { Component, Fragment } from "react";
import AutoSizer from "react-virtualized-auto-sizer";
import { VariableSizeGrid as Grid } from "react-window";

import {
  DEFAULT_TABLE_HEADER_HEIGHT,
  DEFAULT_TABLE_ROW_HEIGHT,
  DROP_SHADOW_HEIGHT,
  MIN_WIDTH_DEFAULT
} from "../constants";

class VirtualizedTable extends Component {
  headerRow = React.createRef();

  grid = React.createRef();

  componentDidUpdate = prevProps => {
    const { columns, rows } = this.props;

    if (prevProps.columns !== columns || prevProps.rows !== rows) {
      this.refreshTable();
    }
  };

  onResize = () => {
    this.refreshTable();
  };

  refreshTable = () => {
    if (this.headerRow.current) {
      this.headerRow.current.resetAfterIndices({
        columnIndex: 0,
        shouldForceUpdate: false
      });
    }

    if (this.grid.current) {
      this.grid.current.resetAfterIndices({
        columnIndex: 0,
        shouldForceUpdate: false
      });
    }
  };

  scrollHandler = ({ scrollLeft, scrollUpdateWasRequested }) => {
    if (!scrollUpdateWasRequested && this.headerRow.current) {
      this.headerRow.current.scrollTo({ scrollLeft, scrollTop: 0 });
    }
  };

  headerRenderer = ({ columnIndex, style }) => {
    const { columns, headerRenderer } = this.props;
    const column = columns[columnIndex];

    return <div style={style}>{headerRenderer(column)}</div>;
  };

  cellRenderer = ({ columnIndex, rowIndex, style }) => {
    const { columns, rows, cellRenderer } = this.props;
    const column = columns[columnIndex];
    const row = rows[rowIndex];
    const cellStyle = {
      ...style,
      borderBottom: rowIndex === rows.length - 1 ? "1px solid #e5e5e5" : "0"
    };

    return <div style={cellStyle}>{cellRenderer(column, row)}</div>;
  };

  getColumnWidth = (index, containerWidth) => {
    const { columns } = this.props;
    const column = columns[index];
    const tableMinWidth = this.getTableMinWidth();
    const colRatio = (column.minWidth || MIN_WIDTH_DEFAULT) / tableMinWidth;
    const width =
      tableMinWidth > containerWidth ? tableMinWidth : containerWidth;

    return width * colRatio;
  };

  getTableMinWidth = () => {
    const { columns } = this.props;
    return columns.reduce(
      (acc, col) => acc + (col.minWidth || MIN_WIDTH_DEFAULT),
      0
    );
  };

  render = () => {
    const {
      columns,
      defaultHeaderHeight,
      defaultHeight,
      defaultRowHeight,
      defaultWidth,
      emptyRenderer,
      headerOverflow,
      headerRenderer,
      rows
    } = this.props;
    const headerHeight = defaultHeaderHeight || DEFAULT_TABLE_HEADER_HEIGHT;
    const rowHeight = defaultRowHeight || DEFAULT_TABLE_ROW_HEIGHT;

    return (
      <AutoSizer
        defaultHeight={defaultHeight}
        defaultWidth={defaultWidth}
        onResize={this.onResize}
      >
        {({ height, width }) => (
          <Fragment>
            {headerRenderer && (
              <Grid
                ref={this.headerRow}
                height={headerHeight + DROP_SHADOW_HEIGHT}
                width={width}
                columnCount={columns.length}
                columnWidth={index => this.getColumnWidth(index, width)}
                rowCount={1}
                rowHeight={() => headerHeight}
                style={
                  !headerOverflow
                    ? { overflow: "hidden" }
                    : {
                        overflow: "hidden",
                        marginBottom: `-${headerOverflow}px`,
                        paddingBottom: `${headerOverflow +
                          headerHeight +
                          DROP_SHADOW_HEIGHT}px`,
                        pointerEvents: "none",
                        zIndex: "1"
                      }
                }
              >
                {this.headerRenderer}
              </Grid>
            )}
            {emptyRenderer && !rows.length ? (
              <div style={{ width: `${width}px` }}>{emptyRenderer()}</div>
            ) : (
              <Grid
                ref={this.grid}
                onScroll={this.scrollHandler}
                height={headerRenderer ? height - headerHeight : height}
                width={width}
                columnCount={columns.length}
                columnWidth={index => this.getColumnWidth(index, width)}
                rowCount={rows.length}
                rowHeight={() => rowHeight}
                style={{
                  top: headerRenderer ? "-5px" : "initial",
                  overflowX: width > this.getTableMinWidth() ? "hidden" : "auto"
                }}
              >
                {this.cellRenderer}
              </Grid>
            )}
          </Fragment>
        )}
      </AutoSizer>
    );
  };
}

VirtualizedTable.propTypes = {
  cellRenderer: PropTypes.func.isRequired,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  defaultHeaderHeight: PropTypes.number,
  defaultHeight: PropTypes.number,
  defaultRowHeight: PropTypes.number,
  defaultWidth: PropTypes.number,
  emptyRenderer: PropTypes.func,
  headerRenderer: PropTypes.func,
  headerOverflow: PropTypes.number,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired
};

VirtualizedTable.defaultProps = {
  defaultHeaderHeight: undefined,
  defaultHeight: undefined,
  defaultRowHeight: undefined,
  defaultWidth: undefined,
  emptyRenderer: undefined,
  headerRenderer: undefined,
  headerOverflow: undefined
};

export default VirtualizedTable;
